/**
 * The firm library on the wire (firm-model spec §3.1 item 6, §8; stage A5). Library rows
 * come in three scopes: `global` (the curated catalog), `org` (the firm library, shared by
 * someone with `edit_library`) and `user` (a member's own saved components, "My
 * components"). Search and browse return all three the caller may see, labeled by scope;
 * the panel's Library page reads the firm's list. A new field is one edit here (type +
 * schema), then `npm run contract:sync`.
 */
import { z } from "zod";
import { expectParity } from "../parity.js";

export type LibraryScope = "global" | "org" | "user";

const libraryScopeSchema = z.enum(["global", "org", "user"]);

/** The browse group a firm-library row sits in (a template's section, an element's kind),
 * and the one a user's own saved component sits in. Both pin ahead of the curated groups. */
export const FIRM_LIBRARY_GROUP = "Firm library";
export const MY_COMPONENTS_GROUP = "Saved";

/** One row of the panel's Library page. */
export type FirmLibraryItem = {
  id: string;
  type: "slide_template" | "element";
  name: string;
  description: string | null;
  /** Who shared it (org rows) or null: unknown, or since deleted ("a former member"). */
  sharedByEmail: string | null;
  createdAt: string;
};

export const firmLibraryItemSchema = z.object({
  id: z.string(),
  type: z.enum(["slide_template", "element"]),
  name: z.string(),
  description: z.string().nullable(),
  sharedByEmail: z.string().nullable(),
  createdAt: z.string(),
});

/** `GET /v1/firm/library`: the firm library, the caller's own saved components, and the
 * firm's saved-component usage against its limit (null = unlimited). */
export type FirmLibraryList = {
  firm: FirmLibraryItem[];
  mine: FirmLibraryItem[];
  used: number;
  limit: number | null;
};

export const firmLibraryListSchema = z.object({
  firm: z.array(firmLibraryItemSchema),
  mine: z.array(firmLibraryItemSchema),
  used: z.number().int(),
  limit: z.number().int().nullable(),
});

/** `PATCH /v1/firm/library/:id` (`edit_library`): rename a firm-library row. */
export type FirmLibraryRenameRequest = { name: string };

export const firmLibraryRenameRequestSchema = z.object({ name: z.string().trim().min(1).max(120) });

expectParity<z.infer<typeof libraryScopeSchema>, LibraryScope>(true);
expectParity<z.infer<typeof firmLibraryItemSchema>, FirmLibraryItem>(true);
expectParity<z.infer<typeof firmLibraryListSchema>, FirmLibraryList>(true);
expectParity<z.infer<typeof firmLibraryRenameRequestSchema>, FirmLibraryRenameRequest>(true);
